import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import axios from 'axios'
import Checkbox from 'components/Checkbox/checkbox'
import FbButton from 'components/FbUI/FbButton'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { EmailSignature } from 'models/emailIntegration'
import { Pencil, Trash2 } from 'lucide-react'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownText } from 'components/OverflownText'

/** Flatten an HTML clipboard payload into plain text, preserving line breaks. */
function htmlToPlainText(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html')
  // Safari can drop a trailing <br> when reading innerText, so promote
  // <br> to literal newlines first.
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  return doc.body.innerText
}

const SIGNATURES_KEY = ['email-signatures']

// KEEP IN SYNC with MAX_SIGNATURE_BYTES in
// server/email_integration/services/signature_sanitizer.py
const MAX_SIGNATURE_BYTES = 8 * 1024
const utf8Encoder = new TextEncoder()
const byteLength = (s: string): number => utf8Encoder.encode(s).length

export function EmailIntegrationSignatures() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: SIGNATURES_KEY,
    queryFn: async ({ signal }) => {
      const [err, res] = await to(
        api.emailIntegration.getEmailSignatureList(signal)
      )
      if (err) throw err
      return res
    },
  })

  const signatures = data?.results ?? []

  const handleSaved = () => {
    setAdding(false)
    setEditingId(null)
    queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
  }

  const descriptionText =
    'Add and manage your signatures. You can have multiple — set one as the default.'

  return (
    <div className="flex flex-col gap-4 w-full overflow-y-auto">
      <div className="flex items-start justify-between">
        <OverflownText
          tooltipText={descriptionText}
          maxLines={4}
          placement="bottom"
          className="text-[#666]"
        >
          {descriptionText}
        </OverflownText>

        {!adding && (
          <FbButton
            onClick={() => {
              setAdding(true)
              setEditingId(null)
            }}
          >
            Add signature
          </FbButton>
        )}
      </div>

      {adding && (
        <SignatureEditor
          onCancel={() => setAdding(false)}
          onSaved={handleSaved}
        />
      )}

      {isFetching && signatures.length === 0 ? (
        <div className="text-sm text-fg-secondary">Loading signatures…</div>
      ) : signatures.length === 0 && !adding ? (
        <div className="rounded-md border border-line bg-surface p-4 text-sm text-fg-secondary italic">
          You don&apos;t have any signatures yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {signatures.map((sig) =>
            editingId === sig.id ? (
              <SignatureEditor
                key={sig.id}
                signature={sig}
                onCancel={() => setEditingId(null)}
                onSaved={handleSaved}
              />
            ) : (
              <SignatureItem
                key={sig.id}
                signature={sig}
                onEdit={() => setEditingId(sig.id)}
              />
            )
          )}
        </div>
      )}
    </div>
  )
}

function SignatureItem({
  signature,
  onEdit,
}: {
  signature: EmailSignature
  onEdit: () => void
}) {
  const api = useApiService()
  const queryClient = useQueryClient()

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.setDefaultEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Set as default')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to set default'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.deleteEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Signature deleted')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to delete'),
  })

  const handleDelete = async () => {
    const ok = await dialogConfirm(
      signature.is_default
        ? 'This is the default signature. Another will be promoted automatically.'
        : 'This signature will be removed.',
      'Delete signature?',
      '',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (ok) deleteMutation.mutate()
  }

  return (
    <div className="rounded-md border border-line bg-surface p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-fg truncate">
            {signature.name || 'Untitled'}
          </span>
          {signature.is_default && (
            <span className="text-xxs px-2 py-0.5 rounded-full bg-brand/10 text-brand">
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!signature.is_default && (
            <button
              type="button"
              onClick={() => setDefaultMutation.mutate()}
              disabled={setDefaultMutation.isPending}
              className="text-xs text-brand hover:underline px-2 py-1 disabled:opacity-50"
              title="Set as default"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-surface-hover text-fg-secondary hover:text-fg"
            title="Edit"
          >
            <Pencil className="size-4" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="p-1 rounded hover:bg-surface-hover text-fg-secondary hover:text-red-600 disabled:opacity-50"
            title="Delete"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      <div className="border border-line rounded p-3 bg-surface-hover text-sm overflow-auto whitespace-pre-wrap">
        {signature.html}
      </div>
    </div>
  )
}

function SignatureEditor({
  signature,
  onCancel,
  onSaved,
}: {
  signature?: EmailSignature
  onCancel: () => void
  onSaved: () => void
}) {
  const api = useApiService()
  const isEditing = !!signature

  const [name, setName] = useState(signature?.name ?? '')
  const [html, setHtml] = useState(signature?.html ?? '')
  const [isDefault, setIsDefault] = useState(signature?.is_default ?? false)
  const sizeBytes = useMemo(() => byteLength(html), [html])
  const isOverByteLimit = sizeBytes > MAX_SIGNATURE_BYTES

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing && signature) {
        const [err, result] = await to(
          api.emailIntegration.updateEmailSignature(signature.id, {
            name,
            html,
            ...(isDefault && !signature.is_default ? { is_default: true } : {}),
          })
        )
        if (err) throw err
        return result
      }
      const [err, result] = await to(
        api.emailIntegration.createEmailSignature({
          name,
          html,
          is_default: isDefault,
        })
      )
      if (err) throw err
      return result
    },
    onSuccess: (result) => {
      toast.success(isEditing ? 'Signature saved' : 'Signature created')
      result?.warnings?.forEach((w) => toast.warning(w))
      onSaved()
    },
    onError: (err) => {
      const detail = axios.isAxiosError<{ detail?: string }>(err)
        ? err.response?.data?.detail
        : undefined
      toast.error(detail ?? 'Failed to save signature')
    },
  })

  return (
    <div className="rounded-md border border-line bg-surface p-4 flex flex-col gap-3">
      <div className="text-sm font-medium text-fg">
        {isEditing ? 'Edit signature' : 'New signature'}
      </div>
      <label className="flex flex-col gap-1 text-xs text-fg-secondary">
        Label
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Default, Outreach, Internal"
          className={cn(
            'w-full rounded border border-line bg-surface px-3 py-2 text-sm text-fg',
            'focus:outline-none focus:border-brand'
          )}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-secondary">
        Signature
        <span className="text-fg-muted font-normal">
          Plain text only. Paste from Gmail or Outlook — formatting will be
          flattened automatically.
        </span>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          onPaste={(e) => {
            const htmlClipboard = e.clipboardData.getData('text/html')
            if (htmlClipboard) {
              e.preventDefault()
              const flattened = htmlToPlainText(htmlClipboard)
              const target = e.currentTarget
              const start = target.selectionStart ?? html.length
              const end = target.selectionEnd ?? html.length
              const next = html.slice(0, start) + flattened + html.slice(end)
              setHtml(next)
            }
          }}
          rows={6}
          className={cn(
            'w-full rounded border bg-surface px-3 py-2 text-sm font-mono text-fg',
            'focus:outline-none',
            isOverByteLimit
              ? 'border-red-500 focus:border-red-500'
              : 'border-line focus:border-brand'
          )}
          placeholder={`Jane Smith\nSenior Account Executive · Acme\njane@acme.com`}
        />
      </label>
      <div
        className={cn(
          'flex items-center justify-between text-xxs',
          isOverByteLimit ? 'text-red-600' : 'text-fg-secondary'
        )}
      >
        <span>
          {isOverByteLimit
            ? `Signature exceeds the ${MAX_SIGNATURE_BYTES / 1024} KB limit. Trim it before saving.`
            : ''}
        </span>
        <span aria-live="polite">
          {(sizeBytes / 1024).toFixed(1)} / {MAX_SIGNATURE_BYTES / 1024} KB
        </span>
      </div>
      {html.trim() !== '' && (
        <div className="border border-line rounded p-3 bg-surface-hover text-sm overflow-auto whitespace-pre-wrap">
          {html}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <label className="flex items-center gap-2 text-xs text-fg-secondary cursor-pointer">
          <Checkbox
            checked={isDefault}
            disabled={signature?.is_default}
            onChange={(e) => setIsDefault(e.currentTarget.checked)}
          />
          <span>Set as default</span>
        </label>
        <div className="flex items-center gap-2">
          <FbButton
            variant="secondary"
            onClick={onCancel}
            disabled={saveMutation.isPending}
          >
            Cancel
          </FbButton>
          <FbButton
            onClick={() => saveMutation.mutate()}
            disabled={
              saveMutation.isPending || html.trim() === '' || isOverByteLimit
            }
          >
            {saveMutation.isPending ? 'Saving…' : isEditing ? 'Save' : 'Create'}
          </FbButton>
        </div>
      </div>
    </div>
  )
}
